/*
 * PDP drawers: progressive enhancement only.
 *
 * The drawers already work from HTML alone: every drawer is a <dialog>, opened
 * and closed by <button command="show-modal|close" commandfor="…"> (Invoker
 * Commands), and every colour/size tile is a real <a href>. This file only:
 *   1. back-fills Invoker Commands in browsers that don't have them yet
 *      (Safari/iOS < 26.2, Chrome < 135, Firefox < 144),
 *   2. back-fills closedby="any" (tap outside to close) where unsupported,
 *   3. adds the design's select → preview → Confirm Selection flow to the
 *      colour and size drawers,
 *   4. closes any open PDP drawer when the cart drawer opens,
 *   6. animates <details> accordions closed in every browser (see below).
 *
 * Everything is delegated from `document`, so there is nothing to set up per
 * element, nothing to clean up, and it keeps working after the theme editor
 * re-renders the section. State lives in the DOM (data-selected, the Confirm
 * link's href), not in JS variables.
 */
(() => {
  const root = document.documentElement;
  if (root.hasAttribute('data-pdp-drawers')) return;
  root.setAttribute('data-pdp-drawers', '');

  // Enables the CSS first-open animation (@starting-style) for <details> rows and
  // accordions in the info column only AFTER the page has painted, so the ones
  // that start open don't animate in on load. One attribute write, no reads.
  requestAnimationFrame(() => requestAnimationFrame(() => root.setAttribute('data-pdp-motion', '')));

  // Order Swatch "In your cart" (one per fabric). Liquid sets the state on load;
  // every cart change since then is published as cartUpdate, so read which
  // variants are in the cart from that payload — the full cart when it has one
  // (drawer quantity change / remove), else the cart-drawer section the add or
  // refresh response carried — and update every [data-swatch-order] button.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const data = event && event.cartData;
      if (!data) return;
      let inCart;
      if (Array.isArray(data.items) && 'item_count' in data) {
        inCart = new Set(data.items.map((item) => String(item.variant_id)));
      } else if (data.sections && data.sections['cart-drawer']) {
        const drawer = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        inCart = new Set(
          Array.from(drawer.querySelectorAll('[data-quantity-variant-id]'), (el) => el.dataset.quantityVariantId)
        );
      } else {
        return;
      }
      document.querySelectorAll('[data-swatch-order]').forEach((button) => {
        const isInCart = inCart.has(button.dataset.swatchOrder);
        button.toggleAttribute('data-in-cart', isInCart);
        button.disabled = isInCart;
      });
    });
  }

  const supportsCommands = 'command' in HTMLButtonElement.prototype;
  const supportsClosedBy = 'closedBy' in HTMLDialogElement.prototype;

  const isModifiedClick = (event) =>
    event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

  // 3. Mark a tile as the drawer's current choice and mirror it into the
  //    preview image, the footer thumb/name and the Confirm link.
  function selectTile(drawer, tile) {
    const previous = drawer.querySelector('[data-option-tile][data-selected]');
    if (previous) previous.removeAttribute('data-selected');
    tile.setAttribute('data-selected', '');

    const preview = drawer.querySelector('[data-option-preview]');
    const previewSrc = tile.dataset.preview;
    if (preview && previewSrc && preview.getAttribute('src') !== previewSrc) {
      preview.classList.add('is-loading');
      const done = () => preview.classList.remove('is-loading');
      preview.addEventListener('load', done, { once: true });
      preview.addEventListener('error', done, { once: true });
      preview.src = previewSrc;
    }

    const thumb = drawer.querySelector('[data-option-thumb]');
    if (thumb && tile.dataset.thumb) thumb.src = tile.dataset.thumb;

    const name = drawer.querySelector('[data-option-name]');
    if (name) name.textContent = tile.dataset.name || '';

    const confirm = drawer.querySelector('[data-option-confirm]');
    if (confirm) confirm.href = tile.href;
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // 1. Invoker Commands fallback.
    if (!supportsCommands) {
      const invoker = target.closest('button[commandfor]');
      if (invoker) {
        const dialog = document.getElementById(invoker.getAttribute('commandfor'));
        const command = invoker.getAttribute('command');
        if (dialog instanceof HTMLDialogElement) {
          if (command === 'show-modal' && !dialog.open) dialog.showModal();
          if (command === 'close' && dialog.open) dialog.close();
        }
      }
    }

    // 2. closedby="any" fallback. A click whose target is the dialog element
    //    itself hit either the backdrop or the dialog's own padding; only a
    //    point outside the dialog box is the backdrop.
    if (!supportsClosedBy && target instanceof HTMLDialogElement && target.open && target.getAttribute('closedby') === 'any') {
      const box = target.getBoundingClientRect();
      const outside =
        event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
      if (outside) target.close();
      return;
    }

    // 3a. Confirm Selection: if the choice is still the page we're on, there
    //     is nothing to navigate to, so close instead of reloading.
    const confirm = target.closest('[data-option-confirm]');
    if (confirm) {
      const drawer = confirm.closest('dialog');
      const current = drawer && drawer.querySelector('[data-option-tile][aria-current="page"]');
      if (drawer && (!current || confirm.pathname === current.pathname)) {
        event.preventDefault();
        drawer.close();
      }
      return;
    }

    // 3b. "More Info" also selects its fabric in the list underneath.
    const more = target.closest('.pdp-colour-option__more');
    if (more) {
      const tile = more.parentElement && more.parentElement.querySelector('[data-option-tile]');
      const drawer = tile && tile.closest('dialog');
      if (drawer) selectTile(drawer, tile);
      return;
    }

    // 3c. A plain tap on a tile selects it instead of navigating straight
    //     away. Modified clicks (new tab/window) keep normal link behaviour.
    const tile = target.closest('[data-option-tile]');
    if (tile && !isModifiedClick(event) && !event.defaultPrevented) {
      const drawer = tile.closest('dialog');
      if (!drawer) return;
      event.preventDefault();
      selectTile(drawer, tile);
    }
  });

  // 4. The cart drawer opening (after add to cart) closes PDP drawers so they
  //    don't stack under it.
  document.addEventListener('cart-drawer:open', () => {
    document.querySelectorAll('dialog.pdp-drawer[open]').forEach((dialog) => dialog.close());
  });

  // 5. No focus ring after closing a drawer by touch/mouse. On close the browser
  //    returns focus to whatever opened the drawer ("More Info", the row button,
  //    or the dialog underneath on iOS), and mobile browsers draw a ring on it.
  //    For pointer users only, focus moves to the heading of the drawer still open
  //    underneath (headings never show a ring), or is dropped if none is open.
  //    Keyboard users keep the browser's normal focus return and visible ring.
  let lastInputWasPointer = false;
  document.addEventListener('pointerdown', () => { lastInputWasPointer = true; }, true);
  document.addEventListener('keydown', () => { lastInputWasPointer = false; }, true);

  // `close` doesn't bubble, so listen in the capture phase.
  document.addEventListener('close', (event) => {
    const closed = event.target;
    if (!lastInputWasPointer || !(closed instanceof HTMLDialogElement) || !closed.classList.contains('pdp-drawer')) return;
    requestAnimationFrame(() => {
      const openDrawers = document.querySelectorAll('dialog.pdp-drawer[open]');
      const underneath = openDrawers[openDrawers.length - 1];
      const heading = underneath && underneath.querySelector('[autofocus]');
      if (heading) {
        heading.focus({ preventScroll: true });
      } else if (document.activeElement instanceof HTMLElement && document.activeElement.closest('[commandfor]')) {
        document.activeElement.blur();
      }
    });
  }, true);

  // 6. Accordions close with the same 0.35s slide they open with, everywhere.
  //    Opening is CSS only (grid rows 0fr -> 1fr, @starting-style). Closing in
  //    CSS needs ::details-content (Safari/iPadOS 18.4+, Chrome 131+); without
  //    it the content just vanished. So a click on an open summary marks the
  //    <details> [data-closing] (CSS slides it to 0fr) and drops [open] when the
  //    slide ends. Rows in an exclusive group (<details name>) that the browser
  //    would snap shut when another opens are slid shut the same way.
  //    Without JS, or with reduced motion, the native toggle is untouched.
  const ACCORDION = '.pdp-info-acc, .pdp-ship-option';
  const PANEL = ':scope > .pdp-info-acc__panel, :scope > .pdp-ship-option__details';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function slideClosed(details, done) {
    const panel = details.querySelector(PANEL);
    if (!panel || reducedMotion.matches) {
      details.open = false;
      if (done) done();
      return;
    }
    details.setAttribute('data-closing', '');
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      panel.removeEventListener('transitionend', onEnd);
      // Re-opened while closing (data-closing removed): leave it open.
      if (details.hasAttribute('data-closing')) {
        details.removeAttribute('data-closing');
        details.open = false;
      }
      if (done) done();
    };
    const onEnd = (event) => {
      if (event.target === panel && event.propertyName === 'grid-template-rows') finish();
    };
    panel.addEventListener('transitionend', onEnd);
    // Fallback if transitionend never fires (e.g. the row is display:none).
    setTimeout(finish, 450);
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || isModifiedClick(event)) return;
    const summary = event.target.closest('summary');
    const details = summary && summary.parentElement;
    if (!details || !details.matches(ACCORDION)) return;

    if (details.hasAttribute('data-closing')) {
      // Clicked again mid-close: slide back open.
      event.preventDefault();
      details.removeAttribute('data-closing');
      return;
    }
    if (details.open) {
      event.preventDefault();
      slideClosed(details);
      return;
    }
    // Opening a row in an exclusive group: slide the open sibling shut instead
    // of letting the browser snap it. Its name is lifted so the browser doesn't
    // close it first, and put back once it has closed.
    const group = details.getAttribute('name');
    if (!group || reducedMotion.matches) return;
    document.querySelectorAll(`details[name="${CSS.escape(group)}"][open]`).forEach((other) => {
      if (other === details) return;
      other.removeAttribute('name');
      slideClosed(other, () => other.setAttribute('name', group));
    });
  });
})();
